import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { softwareWatchApi } from '../../lib/software-watch-api'

/** One checkbox row in the scope tree (module-level so it isn't recreated
 *  on every render). */
function ScopeRow({ label, indent = false, muted = false, checked, disabled, onToggle }) {
  return (
    <label
      className={
        'flex items-center gap-2 py-1 cursor-pointer select-none ' +
        (indent ? 'pl-6 ' : '') +
        (muted ? 'opacity-50 ' : '')
      }
    >
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-[color:var(--color-rule-strong)] accent-[color:var(--color-primary)] cursor-pointer"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
      />
      <span className="text-sm text-[color:var(--color-ink)] font-mono">{label}</span>
    </label>
  )
}

/**
 * Hierarchical, multi-select scope tree for a SoftwareWatch key.
 *
 *   *                          → grant everything
 *   <domain>                   → whole domain
 *   <domain>/<resource>        → one resource
 *
 * `value` is an array of scope strings; `onChange(next)` receives the new
 * array. Selecting `*` collapses to `['*']` (and disables the rest, since it
 * already grants all). Grantable scopes come from the backend catalog.
 */
export function ScopeSelector({ value = [], onChange }) {
  const catalogQuery = useQuery({
    queryKey: ['sw-catalog'],
    queryFn: softwareWatchApi.catalog,
    staleTime: 5 * 60 * 1000,
  })

  const groups = useMemo(() => {
    const byDomain = new Map()
    for (const r of catalogQuery.data?.resources ?? []) {
      if (!byDomain.has(r.domain)) byDomain.set(r.domain, [])
      byDomain.get(r.domain).push(r)
    }
    return [...byDomain.entries()]
      .map(([domain, resources]) => ({ domain, resources }))
      .sort((a, b) => a.domain.localeCompare(b.domain))
  }, [catalogQuery.data])

  const set = new Set(value)
  const all = set.has('*')

  function toggle(scope) {
    if (scope === '*') {
      onChange(all ? [] : ['*'])
      return
    }
    const next = new Set(value)
    next.delete('*') // granular selection turns off the wildcard

    // Parent (domain) row → grant / revoke the WHOLE domain. Turning on stores
    // the compact domain scope (covers current + future resources); turning off
    // clears the domain scope AND any individually-picked children.
    const domainGroup = groups.find((g) => g.domain === scope)
    if (domainGroup) {
      const children = domainGroup.resources.map((r) => r.scope_string)
      const granted =
        next.has(scope) ||
        (children.length > 0 && children.every((c) => next.has(c)))
      next.delete(scope)
      children.forEach((c) => next.delete(c))
      if (!granted) next.add(scope)
      onChange([...next])
      return
    }

    // Child (resource) row. If the whole domain is currently granted compactly,
    // expand it into individual resource scopes first so this one can be turned
    // off without losing the rest — keeping children toggleable under a checked
    // parent. Then flip this child, and re-collapse to the compact domain scope
    // once every child is back on.
    const parent = groups.find((g) =>
      g.resources.some((r) => r.scope_string === scope),
    )
    if (parent && next.has(parent.domain)) {
      next.delete(parent.domain)
      parent.resources.forEach((r) => next.add(r.scope_string))
    }
    if (next.has(scope)) next.delete(scope)
    else next.add(scope)
    if (parent) {
      const children = parent.resources.map((r) => r.scope_string)
      if (children.length > 0 && children.every((c) => next.has(c))) {
        children.forEach((c) => next.delete(c))
        next.add(parent.domain)
      }
    }
    onChange([...next])
  }

  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-3 max-h-64 overflow-y-auto">
      {catalogQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">Loading scopes…</p>
      ) : catalogQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          Could not load the scope catalogue.
        </p>
      ) : (
        <>
          <ScopeRow label="* — grant all resources" checked={all} onToggle={() => toggle('*')} />
          <div className="my-2 border-t border-[color:var(--color-rule)]" />
          {groups.map((g) => {
            // The parent is "checked" when the whole domain is granted — either
            // via the compact domain scope, or because every child happens to
            // be individually selected. Children stay INDIVIDUALLY toggleable:
            // they show checked when the domain scope covers them, but aren't
            // disabled, so one can be switched off (which expands the grant —
            // see `toggle`). Only `*` mutes/disables the tree.
            const children = g.resources.map((r) => r.scope_string)
            const domainGranted =
              all ||
              set.has(g.domain) ||
              (children.length > 0 && children.every((c) => set.has(c)))
            return (
              <div key={g.domain} className="mb-1">
                <ScopeRow
                  label={g.domain}
                  muted={all}
                  checked={domainGranted}
                  disabled={all}
                  onToggle={() => toggle(g.domain)}
                />
                {g.resources.map((r) => (
                  <ScopeRow
                    key={r.scope_string}
                    label={`${g.domain}/${r.resource}`}
                    indent
                    muted={all}
                    checked={all || set.has(g.domain) || set.has(r.scope_string)}
                    disabled={all}
                    onToggle={() => toggle(r.scope_string)}
                  />
                ))}
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
